import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface NewProjectModalProps {
  open: boolean;
  onClose: () => void;
}

export function NewProjectModal({ open, onClose }: NewProjectModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!open) return null;

  const handleFlow1 = () => {
    onClose();
    navigate('/new-project');
  };

  const handleFlow2 = () => {
    onClose();
    navigate('/new-project?flow=polygon');
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-[820px] mx-4 bg-[#1E1E1E] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <h2 className="text-lg font-semibold text-white">{t('projects.newProjectModal.title')}</h2>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition"
          >
            <svg
              className="w-5 h-5"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* Cards */}
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Flow 1 — Segmented 3D Model */}
          <div className="flex flex-col bg-[#232323] border border-white/5 rounded-xl overflow-hidden hover:border-lime-400/50 transition group">
            <div className="flex-1 p-5">
              {/* Icon + Title */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-500/15 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-blue-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 002 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0022 16z" />
                    <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                    <line x1="12" y1="22.08" x2="12" y2="12" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white">{t('projects.newProjectModal.flow1Title')}</h3>
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-gray-400 leading-relaxed mb-4">{t('projects.newProjectModal.flow1Desc')}</p>

              {/* Examples */}
              <div>
                <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">
                  {t('projects.newProjectModal.flow1Examples')}
                </p>
                <p className="text-[10px] text-gray-500 mb-2">{t('projects.newProjectModal.flow1ExamplesHint')}</p>
              </div>
            </div>

            {/* Button */}
            <div className="px-5 pb-5">
              <button
                onClick={handleFlow1}
                className="w-full h-9 rounded-lg bg-white/5 border border-white/10 text-sm text-white font-medium hover:bg-lime-400 hover:text-black hover:border-lime-400 transition"
              >
                {t('projects.newProjectModal.getStarted')}
              </button>
            </div>
          </div>

          {/* Flow 2 — Polygon Flow */}
          <div className="flex flex-col bg-[#232323] border border-white/5 rounded-xl overflow-hidden hover:border-lime-400/50 transition group">
            <div className="flex-1 p-5">
              {/* Icon + Title */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-lime-400/15 flex items-center justify-center">
                  <svg
                    className="w-5 h-5 text-lime-400"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="12 2 22 8.5 22 15.5 12 22 2 15.5 2 8.5" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-white">{t('projects.newProjectModal.flow2Title')}</h3>
                </div>
              </div>

              {/* Description */}
              <p className="text-xs text-gray-400 leading-relaxed mb-4">{t('projects.newProjectModal.flow2Desc')}</p>

              {/* Placeholder hint */}
              <div className="flex items-start gap-2.5 p-3 bg-lime-400/5 border border-lime-400/10 rounded-lg">
                <svg
                  className="w-4 h-4 text-lime-400 shrink-0 mt-0.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="16" x2="12" y2="12" />
                  <line x1="12" y1="8" x2="12.01" y2="8" />
                </svg>
                <p className="text-[11px] text-lime-400/80 leading-relaxed">
                  {t('projects.newProjectModal.flow2Hint')}
                </p>
              </div>
            </div>

            {/* Button */}
            <div className="px-5 pb-5">
              <button
                onClick={handleFlow2}
                className="w-full h-9 rounded-lg bg-white/5 border border-white/10 text-sm text-white font-medium hover:bg-lime-400 hover:text-black hover:border-lime-400 transition"
              >
                {t('projects.newProjectModal.getStarted')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
